import { useState, useRef, useEffect, DragEvent } from 'react';
import { 
  Upload, FileText, Download, Trash2, Search, 
  FolderOpen, File, Image, Sheet, 
  Film, Archive, X, Calendar,
  User, Grid, List, Check, AlertCircle, Clock, Edit
} from 'lucide-react';
import { projectDocumentsService } from '../../services/projectDocuments';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

interface Document {
  id: string;
  name: string;
  type: string;
  size: number;
  uploadedBy: string;
  uploadedAt: string;
  category: string;
  status: 'processing' | 'ready' | 'error';
  url?: string;
}

function mapApiToDocument(item: import('../../services/projectDocuments').ProjectDocumentItem): Document {
  return {
    id: String(item.id),
    name: item.name,
    type: item.type || 'document',
    size: item.size,
    uploadedBy: item.uploaded_by || '',
    uploadedAt: item.uploaded_at || '',
    category: item.category || 'General',
    status: (item.status as 'processing' | 'ready' | 'error') || 'ready',
    url: item.url || undefined,
  };
}

interface DocumentsSectionProps {
  projectId?: number;
}

export function DocumentsSection({ projectId }: DocumentsSectionProps) {
  // Logged-in user populates `uploadedBy` on new docs — replaces the old
  // hardcoded 'Sarah Chen' literal that lied about who uploaded the file.
  const { user: currentUser } = useAuth();
  const [documents, setDocuments] = useState<Document[]>([]);

  useEffect(() => {
    if (!projectId) {
      setDocuments([]);
      return;
    }
    projectDocumentsService
      .list(projectId)
      .then((list) => setDocuments(list.map(mapApiToDocument)))
      .catch(() => {
        toast.error('Failed to load documents');
        // Do NOT fall back to demo data on real projects — see RisksSection.
        setDocuments([]);
      });
  }, [projectId]);
  
  const [isDragging, setIsDragging] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
  const [selectedFileCategory, setSelectedFileCategory] = useState('General');
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const categories = [
    { id: 'all', name: 'All Documents', count: documents.length },
    { id: 'governance', name: 'Governance', count: documents.filter(d => d.category === 'Governance').length },
    { id: 'technical', name: 'Technical', count: documents.filter(d => d.category === 'Technical').length },
    { id: 'risk', name: 'Risk Management', count: documents.filter(d => d.category === 'Risk Management').length },
    { id: 'communications', name: 'Communications', count: documents.filter(d => d.category === 'Communications').length },
    { id: 'financial', name: 'Financial', count: documents.filter(d => d.category === 'Financial').length },
    { id: 'quality', name: 'Quality', count: documents.filter(d => d.category === 'Quality').length },
  ];

  const categoryOptions = [
    'General',
    'Governance',
    'Technical',
    'Risk Management',
    'Communications',
    'Financial',
    'Quality',
    'Planning',
    'Execution',
    'Closure',
  ];

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'pdf':
        return <FileText className="w-8 h-8 text-red-500" />;
      case 'image':
        return <Image className="w-8 h-8 text-purple-500" />;
      case 'spreadsheet':
        return <Sheet className="w-8 h-8 text-green-500" />;
      case 'document':
        return <FileText className="w-8 h-8 text-blue-500" />;
      case 'video':
        return <Film className="w-8 h-8 text-pink-500" />;
      case 'archive':
        return <Archive className="w-8 h-8 text-yellow-500" />;
      default:
        return <File className="w-8 h-8 text-gray-500" />;
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setUploadingFiles(files);
      setShowUploadModal(true);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setUploadingFiles(files);
      setShowUploadModal(true);
    }
  };

  const handleUploadConfirm = () => {
    // Simulate file upload
    const newDocuments = uploadingFiles.map((file, index) => {
      const fileType = file.type.includes('pdf') ? 'pdf' :
                       file.type.includes('image') ? 'image' :
                       file.type.includes('spreadsheet') || file.type.includes('excel') ? 'spreadsheet' :
                       file.type.includes('word') || file.type.includes('document') ? 'document' :
                       'document';

      return {
        id: `${Date.now()}-${index}`,
        name: file.name,
        type: fileType,
        size: file.size,
        uploadedBy:
          (currentUser?.full_name && currentUser.full_name.trim()) ||
          currentUser?.email ||
          'You',
        uploadedAt: new Date().toISOString(),
        category: selectedFileCategory,
        status: 'processing' as const,
      };
    });

    setDocuments([...newDocuments, ...documents]);
    setShowUploadModal(false);
    setUploadingFiles([]);
    
    setNotification({
      message: `${uploadingFiles.length} file${uploadingFiles.length > 1 ? 's' : ''} uploaded successfully`,
      type: 'success'
    });

    // Simulate processing complete
    setTimeout(() => {
      setDocuments(prev => prev.map(doc => 
        newDocuments.find(nd => nd.id === doc.id) 
          ? { ...doc, status: 'ready' as const }
          : doc
      ));
    }, 2000);

    setTimeout(() => setNotification(null), 3000);
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this document?')) {
      setDocuments(documents.filter(doc => doc.id !== id));
      setNotification({ message: 'Document deleted successfully', type: 'success' });
      setTimeout(() => setNotification(null), 3000);
    }
  };

  const handleDownload = (doc: Document) => {
    setNotification({ message: `Downloading ${doc.name}...`, type: 'info' });
    setTimeout(() => setNotification(null), 2000);
  };

  const handleEditOnline = (doc: Document) => {
    setNotification({ message: `Opening ${doc.name} in editor...`, type: 'info' });
    setTimeout(() => setNotification(null), 2000);
    // In a real application, this would open the document in an online editor
    // For example: Microsoft Office Online, Google Docs, or a custom editor
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         doc.uploadedBy.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || 
                           doc.category.toLowerCase() === selectedCategory.toLowerCase();
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-[#E8E9E9]">
      {/* Notification Toast */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-2xl border-2 flex items-center gap-3 animate-in slide-in-from-top-5 ${
          notification.type === 'success' ? 'bg-green-500 border-green-600 text-white' :
          notification.type === 'error' ? 'bg-red-500 border-red-600 text-white' :
          'bg-blue-500 border-blue-600 text-white'
        }`}>
          {notification.type === 'success' ? (
            <Check className="w-5 h-5" />
          ) : notification.type === 'error' ? (
            <AlertCircle className="w-5 h-5" />
          ) : (
            <Clock className="w-5 h-5" />
          )}
          <span className="font-medium">{notification.message}</span>
          <button 
            onClick={() => setNotification(null)}
            className="ml-2 hover:bg-white/20 rounded p-1 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#486581] via-[#5C748E] to-[#486581] border-b-4 border-[#DAA520] px-8 py-8">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl text-white mb-2 flex items-center gap-3">
                <FileText className="w-8 h-8 text-[#DAA520]" />
                Project Documents
              </h1>
              <p className="text-[#C5CDD6] text-lg">
                Centralized document repository for all project files, deliverables, and artifacts
              </p>
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-6 py-3 bg-gradient-to-r from-[#DAA520] to-[#c99a1c] text-white rounded-lg hover:from-[#c99a1c] hover:to-[#b38a19] transition-all shadow-lg flex items-center gap-2"
            >
              <Upload className="w-5 h-5" />
              Upload Documents
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20">
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 bg-[#DAA520]/20 rounded-lg">
                  <FileText className="w-6 h-6 text-[#DAA520]" />
                </div>
                <span className="text-xs text-[#C5CDD6] bg-white/10 px-2 py-1 rounded">Total</span>
              </div>
              <div className="text-3xl text-white mb-1">{documents.length}</div>
              <div className="text-sm text-[#C5CDD6]">Total Documents</div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20">
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <Upload className="w-6 h-6 text-green-400" />
                </div>
                <span className="text-xs text-[#C5CDD6] bg-white/10 px-2 py-1 rounded">This Week</span>
              </div>
              <div className="text-3xl text-white mb-1">12</div>
              <div className="text-sm text-[#C5CDD6]">Recently Uploaded</div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20">
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <FolderOpen className="w-6 h-6 text-blue-400" />
                </div>
                <span className="text-xs text-[#C5CDD6] bg-white/10 px-2 py-1 rounded">Active</span>
              </div>
              <div className="text-3xl text-white mb-1">{categories.length - 1}</div>
              <div className="text-sm text-[#C5CDD6]">Categories</div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20">
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Download className="w-6 h-6 text-purple-400" />
                </div>
                <span className="text-xs text-[#C5CDD6] bg-white/10 px-2 py-1 rounded">Storage</span>
              </div>
              <div className="text-3xl text-white mb-1">2.4 GB</div>
              <div className="text-sm text-[#C5CDD6]">Total Size</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-8 py-8">
        {/* Upload Drop Zone */}
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`mb-8 border-3 border-dashed rounded-xl p-12 transition-all ${
            isDragging
              ? 'border-[#DAA520] bg-[#DAA520]/10 scale-102'
              : 'border-[#5C748E] bg-white hover:border-[#DAA520] hover:bg-[#DAA520]/5'
          }`}
        >
          <div className="text-center">
            <div className="inline-flex p-4 bg-[#486581]/10 rounded-full mb-4">
              <Upload className="w-12 h-12 text-[#486581]" />
            </div>
            <h3 className="text-xl mb-2">Drag and drop files here</h3>
            <p className="text-[#4a5565] mb-4">
              or click the button to browse files from your computer
            </p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-6 py-3 bg-[#486581] text-white rounded-lg hover:bg-[#5C748E] transition-all"
            >
              Select Files
            </button>
            <p className="text-sm text-[#829ab1] mt-4">
              Supported formats: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, PNG, JPG, ZIP (Max 50MB per file)
            </p>
          </div>
        </div>

        {/* Filters and Controls */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-lg transition-all ${
                  selectedCategory === category.id
                    ? 'bg-[#DAA520] text-white'
                    : 'bg-white text-[#4a5565] hover:bg-[#486581]/10 border border-[#5C748E]'
                }`}
              >
                {category.name} ({category.count})
              </button>
            ))}
          </div>

          <div className="flex gap-3">
            {/* Search */}
            <div className="relative">
              <Search className="w-5 h-5 text-[#829ab1] absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 bg-white border border-[#5C748E] rounded-lg focus:border-[#DAA520] focus:outline-none w-64"
              />
            </div>

            {/* View Toggle */}
            <div className="flex gap-2 bg-white border border-[#5C748E] rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${
                  viewMode === 'grid' ? 'bg-[#DAA520] text-white' : 'text-[#4a5565]'
                }`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${
                  viewMode === 'list' ? 'bg-[#DAA520] text-white' : 'text-[#4a5565]'
                }`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Documents Grid/List */}
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-5 gap-4">
            {filteredDocuments.map((doc) => (
              <div
                key={doc.id}
                className="bg-white rounded-lg border border-gray-200 hover:border-[#DAA520] hover:shadow-md transition-all"
              >
                <div className="p-4">
                  {/* Icon and Status */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="p-2 bg-gray-50 rounded-lg">
                      {getFileIcon(doc.type)}
                    </div>
                    {doc.status === 'processing' && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded text-xs flex items-center gap-1">
                        <Clock className="w-3 h-3 animate-spin" />
                      </span>
                    )}
                  </div>

                  {/* Document Name */}
                  <h3 className="text-sm font-medium text-[#2A3B4C] mb-2 line-clamp-2 min-h-[40px]" title={doc.name}>
                    {doc.name}
                  </h3>

                  {/* Category Badge */}
                  <div className="mb-3">
                    <span className="px-2 py-1 bg-[#486581]/10 text-[#486581] rounded text-xs inline-block">
                      {doc.category}
                    </span>
                  </div>

                  {/* Metadata */}
                  <div className="text-xs text-[#829ab1] mb-3 space-y-1">
                    <div className="flex items-center gap-1">
                      <User className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{doc.uploadedBy}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3 flex-shrink-0" />
                      <span>{formatDate(doc.uploadedAt)}</span>
                    </div>
                    <div>{formatFileSize(doc.size)}</div>
                  </div>

                  {/* Actions */}
                  <div className="space-y-2">
                    <button
                      onClick={() => handleDownload(doc)}
                      className="w-full px-3 py-2 bg-[#486581] text-white rounded-lg hover:bg-[#5C748E] transition-all text-xs flex items-center justify-center gap-2"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Download
                    </button>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditOnline(doc)}
                        className="flex-1 px-2 py-1.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-all text-xs flex items-center justify-center gap-1"
                        title="Edit Online"
                      >
                        <Edit className="w-3.5 h-3.5" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(doc.id)}
                        className="px-2 py-1.5 bg-red-50 text-red-600 rounded hover:bg-red-100 transition-all"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-[#5C748E] overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#486581]/5 border-b border-[#5C748E]">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#2A3B4C]">Name</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#2A3B4C]">Category</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#2A3B4C]">Uploaded By</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#2A3B4C]">Date</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#2A3B4C]">Size</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#2A3B4C]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocuments.map((doc) => (
                  <tr key={doc.id} className="border-b border-[#5C748E]/20 hover:bg-[#486581]/5 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {getFileIcon(doc.type)}
                        <span className="font-medium text-[#2A3B4C]">{doc.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-[#486581]/10 text-[#486581] rounded text-xs">
                        {doc.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-[#4a5565]">{doc.uploadedBy}</td>
                    <td className="px-6 py-4 text-[#4a5565]">{formatDate(doc.uploadedAt)}</td>
                    <td className="px-6 py-4 text-[#4a5565]">{formatFileSize(doc.size)}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleDownload(doc)}
                          className="p-2 text-[#486581] hover:bg-[#486581]/10 rounded-lg transition-all"
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditOnline(doc)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                          title="Edit Online"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(doc.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {filteredDocuments.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl border border-[#5C748E]">
            <FileText className="w-16 h-16 text-[#829ab1] mx-auto mb-4" />
            <h3 className="text-xl mb-2">No documents found</h3>
            <p className="text-[#4a5565]">Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-medium">Upload Documents</h2>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadingFiles([]);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Category Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#2A3B4C] mb-2">
                  Document Category
                </label>
                <select
                  value={selectedFileCategory}
                  onChange={(e) => setSelectedFileCategory(e.target.value)}
                  className="w-full px-4 py-2 border border-[#5C748E] rounded-lg focus:border-[#DAA520] focus:outline-none"
                >
                  {categoryOptions.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Files List */}
              <div className="space-y-3">
                <h3 className="font-medium text-[#2A3B4C] mb-3">
                  Files to upload ({uploadingFiles.length})
                </h3>
                {uploadingFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="w-8 h-8 text-[#486581]" />
                      <div>
                        <div className="font-medium text-[#2A3B4C]">{file.name}</div>
                        <div className="text-sm text-[#829ab1]">{formatFileSize(file.size)}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => setUploadingFiles(uploadingFiles.filter((_, i) => i !== index))}
                      className="p-2 hover:bg-white rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4 text-[#829ab1]" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadingFiles([]);
                }}
                className="px-4 py-2 text-[#4a5565] hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUploadConfirm}
                disabled={uploadingFiles.length === 0}
                className="px-6 py-2 bg-[#DAA520] text-white rounded-lg hover:bg-[#c99a1c] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Upload {uploadingFiles.length} File{uploadingFiles.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}